import { useMemo, useState } from 'react';
import type { InvoiceListItem, InvoiceStudentRef } from '@repo/shared-types';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { Button } from '@repo/ui/src/components/ui/button';
import { Checkbox } from '@repo/ui/src/components/ui/checkbox';
import { Input } from '@repo/ui/src/components/ui/input';
import { Skeleton } from '@repo/ui/src/components/ui/skeleton';
import { InvoiceStatusBadge } from '../../lib/invoiceStatus';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/src/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/src/components/ui/table';
import { cn } from '@repo/ui/src/lib/utils';
import { CalendarCheck, FileText, Lock, Plus, Search, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useInvoices } from '../../hooks/useInvoices';
import { useMultiSelect } from '../../hooks/useMultiSelect';
import { useExtrasAccess } from '../../hooks/useExtrasAccess';
import { useUpgradeDialogStore } from '../../stores/upgradeDialogStore';
import { matchesInvoiceSearch } from '../../lib/invoiceSearch';
import { currentInvoiceMonth, defaultInvoiceWindow, monthToWindow } from '../../lib/invoiceWindow';
import { formatVnd } from '../../lib/formatVnd';
import { useServerNow } from '../../hooks/useServerNow';
import { highlightRanges } from '../../lib/searchHighlight';
import { fmtMatchedAt } from '../../lib/matchedAt';
import {
  COUNT_KEY_BY_FILTER,
  invoiceCounts,
  matchesStatusFilter,
  type InvoiceCounts,
  type StatusFilter,
} from '../../lib/invoiceCounts';
import { overdueDays, parseDueDate } from '../../lib/overdueDays';
import { BulkActionsBar } from './components/BulkActionsBar';
import { PageHeader } from '../../components/PageHeader';
import { MonthPicker } from '../../components/pickers/MonthPicker';
import { EmptyState } from '../../components/EmptyState';

type SortKey = 'date-desc' | 'date-asc' | 'amount-desc' | 'amount-asc';

// dd/MM/yyyy in UTC: due dates are wall-clock-as-UTC (packages/shared-types/src/time.ts),
// so formatting in the browser's own zone would shift the day for anyone outside VN.
const DUE_DATE_FMT = new Intl.DateTimeFormat('vi-VN', {
  timeZone: 'UTC',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

// Chip order is the spec's (§2), not STATUS_FILTER_OPTIONS' — money that needs
// attention first, settled last. Labels reuse the existing `status.*` keys.
const STATUS_CHIPS: { filter: StatusFilter; labelKey: string }[] = [
  { filter: 'ALL', labelKey: 'list.filterAll' },
  { filter: 'PENDING', labelKey: 'status.PENDING' },
  { filter: 'OVERDUE', labelKey: 'status.OVERDUE' },
  { filter: 'PARTIAL', labelKey: 'status.PARTIAL' },
  { filter: 'DRAFT', labelKey: 'status.DRAFT' },
  { filter: 'PAID', labelKey: 'status.PAID' },
  { filter: 'CANCELLED', labelKey: 'status.CANCELLED' },
];

function chipCount(counts: InvoiceCounts, filter: StatusFilter): number {
  return filter === 'ALL' ? counts.total : counts[COUNT_KEY_BY_FILTER[filter]];
}

/**
 * `text` with every matched fragment wrapped in `<mark>`, folded the same way
 * the search predicate folds (lib/searchHighlight.ts) — so "ha" lights up the
 * "Hà" it actually matched. Plain text when the query is empty or nothing hit.
 *
 * Renders spans only: this is used inside the phone card's `<button>`, where a
 * `<div>` would be invalid HTML that React does not warn about.
 */
function Highlighted({ text, query }: { text: string; query: string }) {
  const ranges = useMemo(() => highlightRanges(text, query), [text, query]);
  if (ranges.length === 0) return <>{text}</>;

  const out: React.ReactNode[] = [];
  let cursor = 0;
  for (const [i, r] of ranges.entries()) {
    if (r.start > cursor) out.push(text.slice(cursor, r.start));
    out.push(
      <mark key={i} className="rounded-sm bg-attention-tint text-inherit">
        {text.slice(r.start, r.end)}
      </mark>,
    );
    cursor = r.end;
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return <>{out}</>;
}

/**
 * The students behind an invoice, shown ONLY while the search box has
 * something in it.
 *
 * `matchesInvoiceSearch` matches a student's name AND nickname, but neither is
 * otherwise on the row — a teacher typing "han", getting back an invoice
 * addressed to "Trần Văn Hùng" and seeing no "Gia Hân" anywhere would read it
 * as a broken search. This line is the evidence, so it appears with the reason
 * and disappears with it; the resting list is unchanged.
 */
function StudentMatchLine({ students, query }: { students: InvoiceStudentRef[]; query: string }) {
  if (query.trim() === '' || students.length === 0) return null;
  return (
    <span className="mt-0.5 block truncate text-[12px] text-surface-500">
      {students.map((s, i) => (
        <span key={`${s.name}-${i}`}>
          {i > 0 && ' · '}
          <Highlighted text={s.name} query={query} />
          {s.nickname && (
            <>
              {' ('}
              <Highlighted text={s.nickname} query={query} />
              {')'}
            </>
          )}
        </span>
      ))}
    </span>
  );
}

export function InvoicesPage() {
  const { t } = useTranslation('invoices');
  const { t: ti } = useTranslation('invoicing');
  const navigate = useNavigate();
  // Extras three-state: enabled (planAllows && orgSells) -> normal button;
  // !planAllows -> lock icon, opens upgrade dialog; planAllows && !orgSells ->
  // hidden (org's own choice, not an upsell). See useExtrasAccess.
  const { planAllows, enabled: extrasEnabled } = useExtrasAccess();
  const openUpgradeDialog = useUpgradeDialogStore((s) => s.open);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [sortKey, setSortKey] = useState<SortKey>('date-desc');
  const [searchQuery, setSearchQuery] = useState('');
  // Selection bar (design doc §1): ids only, resolved against `invoices` (not
  // `visibleInvoices`) while the filter stays put — see
  // handleStatusFilterChange below for why it doesn't survive a filter
  // change.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Month window (THI-94, narrowed 2026-09-04): the picker always shows a
  // month. On the CURRENT month the window is the non-strict default (this
  // month + every unpaid invoice, however old); any other month is the
  // strict archival view. The "3 tháng gần nhất" default is gone: three
  // months of paid rows buried the month being worked on. `thisMonth` is
  // recomputed every render so a month boundary is picked up for free.
  const thisMonth = currentInvoiceMonth(new Date());
  const [pickedMonth, setPickedMonth] = useState<string>(thisMonth);
  const isDefaultWindow = pickedMonth === thisMonth;
  const invoiceWindow = isDefaultWindow ? defaultInvoiceWindow(new Date()) : monthToWindow(pickedMonth);
  const { data: invoices, isLoading, isError } = useInvoices(invoiceWindow);

  // Changing the status filter can hide rows that are still ticked: a
  // teacher filtering PENDING, selecting 5, then switching to DRAFT would
  // otherwise keep those 5 selected-but-invisible, and they'd ride silently
  // into the next bulk action (PENDING is mark-paid-eligible, so this isn't
  // even a no-op skip). Selection is a transient intent scoped to what's on
  // screen, so any filter that can hide rows clears it outright — the
  // simple, predictable rule, rather than trying to reconcile a selection
  // against a view it was never made under. Done in the filter's own
  // onValueChange (not a useEffect keyed on statusFilter) so it's one
  // event-driven state update, not a second render reacting to the first.
  // sortKey has no equivalent handler: it reorders visibleInvoices but never
  // hides a row, so there's nothing to reconcile.
  function handleStatusFilterChange(next: StatusFilter) {
    setStatusFilter(next);
    setSelectedIds(new Set());
  }

  // Search is exactly the same kind of row-hiding control as the status
  // filter (design doc §3, THI-86's rule) — wired identically, for the same
  // reason: a hidden-but-still-ticked row must never ride silently into a
  // bulk action.
  function handleSearchChange(next: string) {
    setSearchQuery(next);
    setSelectedIds(new Set());
  }

  // Changing the month window is the same kind of row-hiding action as the
  // status filter and search above (THI-86's rule, third instance on this
  // page) — a selection made under June must not silently ride into July.
  function handleMonthPick(next: string) {
    setPickedMonth(next);
    setSelectedIds(new Set());
  }

  function handleResetWindow() {
    setPickedMonth(thisMonth);
    setSelectedIds(new Set());
  }

  // Empty-state kind (design doc 2026-08-22-states §1): `invoices` is already
  // scoped to `invoiceWindow`, so an empty `invoices` means the window itself
  // (default or picked) has nothing — teach only when that's ALSO the
  // out-of-the-box state (default window, no status filter, no search); any
  // filter/window narrowing turns the same empty result into a filterMiss
  // instead. Search takes priority over a plain filterMiss when it's the
  // thing that emptied the visible list, mirroring the same rule used below
  // for handleSearchChange.
  const hasActiveFilter = statusFilter !== 'ALL' || !isDefaultWindow;

  function handleClearFilters() {
    setStatusFilter('ALL');
    setPickedMonth(thisMonth);
    setSelectedIds(new Set());
  }

  const visibleInvoices = useMemo(() => {
    const filtered = (invoices ?? []).filter(
      (inv) => matchesStatusFilter(inv, statusFilter) && matchesInvoiceSearch(inv, searchQuery),
    );
    return [...filtered].sort((a, b) => {
      switch (sortKey) {
        case 'date-asc':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'amount-desc':
          return b.totalAmount - a.totalAmount;
        case 'amount-asc':
          return a.totalAmount - b.totalAmount;
        case 'date-desc':
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });
  }, [invoices, statusFilter, searchQuery, sortKey]);

  const visibleInvoiceIds = useMemo(() => visibleInvoices.map((inv) => inv.id), [visibleInvoices]);
  // Click-anywhere-toggles-a-row + shift-click-ranges (THI-180) once
  // anything is selected — same `selectedIds` this page already tracked, so
  // every other handler below (filter/search/month-change clearing, the
  // BulkActionsBar prop) is untouched.
  const multi = useMultiSelect(visibleInvoiceIds, selectedIds, setSelectedIds);

  // Window-scoped, NOT filtered by the active chip or the search box: the
  // subtitle and the chips describe the window a teacher is looking at, so
  // clicking "Quá hạn" must not renumber every other chip.
  const counts = useMemo(() => invoiceCounts(invoices ?? []), [invoices]);
  const now = useServerNow();

  // Empty-state kind, decided once and rendered once — the table and the card
  // list share it, so neither breakpoint can end up with a different story
  // (and no colSpan has to track the column count). Same precedence as before:
  // teach only in the out-of-the-box state, search before a plain filterMiss.
  const emptyKind: 'teach' | 'searchMiss' | 'filterMiss' | 'error' | null = isLoading
    ? null
    : isError
      ? 'error'
      : (invoices?.length ?? 0) === 0 && !hasActiveFilter && !searchQuery
        ? 'teach'
        : visibleInvoices.length === 0
          ? (searchQuery && (invoices?.length ?? 0) > 0 ? 'searchMiss' : 'filterMiss')
          : null;

  const getStatusBadge = (invoice: Pick<InvoiceListItem, 'status' | 'isOverdue'>) => (
    <InvoiceStatusBadge status={invoice.status} isOverdue={invoice.isOverdue} />
  );

  // THI-189/THI-190: "Loại" chip — chốt sổ, khóa học (TERM), or a
  // manually-created invoice (spec §10.4a, §11.2 item 6).
  const kindLabel = (invoice: Pick<InvoiceListItem, 'kind'>) =>
    invoice.kind === 'CLOSE_MONTH' ? (
      <Badge variant="muted">{t('list.kindClose')}</Badge>
    ) : invoice.kind === 'TERM' ? (
      <Badge variant="muted">{t('list.kindTerm')}</Badge>
    ) : (
      <Badge variant="outline">{t('list.kindManual')}</Badge>
    );

  // "Tự khớp chuyển khoản lúc 09:41 hôm nay" — the beat where the teacher sees
  // the software did the bookkeeping. SEPAY only: a payment they typed in
  // themselves needs no announcing back to them.
  function autoMatchLine(invoice: InvoiceListItem): string | null {
    if (invoice.status !== 'PAID' || invoice.lastPayment?.source !== 'SEPAY') return null;
    const at = fmtMatchedAt(invoice.lastPayment.at, now);
    if (!at) return null;
    return at.date === null
      ? t('autoMatch.today', { time: at.time })
      : t('autoMatch.on', { time: at.time, date: at.date });
  }

  const allVisibleSelected = visibleInvoices.length > 0 && visibleInvoices.every((inv) => selectedIds.has(inv.id));
  const someVisibleSelected = visibleInvoices.some((inv) => selectedIds.has(inv.id));

  function toggleSelectAllVisible() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const inv of visibleInvoices) next.delete(inv.id);
      } else {
        for (const inv of visibleInvoices) next.add(inv.id);
      }
      return next;
    });
  }

  return (
    <div className={cn('mx-auto w-full max-w-6xl p-8', selectedIds.size > 0 && 'pb-40 md:pb-24')}>
      <PageHeader
        size="lg"
        title={t('list.title')}
        subtitleWrap
        subtitle={
          isLoading ? undefined : counts.total === 0 ? (
            t('list.subtitleEmpty')
          ) : (
            <span className="inline-flex flex-wrap items-center gap-x-1.5">
              <span>{t('list.subtitleTotal', { count: counts.total })}</span>
              {counts.pending > 0 && (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="text-attention-foreground">{t('list.subtitlePending', { count: counts.pending })}</span>
                </>
              )}
              {counts.overdue > 0 && (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="text-attention-foreground">{t('list.subtitleOverdue', { count: counts.overdue })}</span>
                </>
              )}
            </span>
          )
        }
        actions={[
          { key: 'close-month', label: ti('closeMonth.open'), icon: CalendarCheck, to: '/invoices/close-month' },
          extrasEnabled
            ? { key: 'create-extra', label: t('list.createExtra'), icon: Plus, to: '/invoices/new', variant: 'outline' }
            : {
                key: 'create-extra',
                label: t('list.createExtra'),
                icon: Lock,
                variant: 'outline',
                onClick: () => openUpgradeDialog('extras'),
                hidden: planAllows,
              },
        ]}
      />

      {/* Chips + month window. The chip row scrolls horizontally on phones and
          bleeds through the page's p-8 gutter so the first chip is flush with
          the title above it. Chip paddings are pinned in px: --spacing is
          0.2rem app-wide, so px-3/py-1.5 would render a ~28px chip. */}
      <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
        <div
          role="group"
          aria-label={t('list.filterGroupLabel')}
          className="-mx-8 flex gap-2 overflow-x-auto px-8 [scrollbar-width:none] md:mx-0 md:min-w-0 md:flex-1 md:flex-wrap md:overflow-visible md:px-0"
        >
          {STATUS_CHIPS.map(({ filter, labelKey }) => {
            const n = chipCount(counts, filter);
            // Đã huỷ only appears when there is something cancelled to look at.
            if (filter === 'CANCELLED' && n === 0) return null;
            const active = statusFilter === filter;
            return (
              <button
                key={filter}
                type="button"
                aria-pressed={active}
                onClick={() => handleStatusFilterChange(filter)}
                className={cn(
                  'inline-flex min-h-11 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-[14px] text-[13px] font-medium transition-colors md:min-h-[36px]',
                  active
                    ? 'border-brand-600 bg-brand-600 text-white'
                    : 'border-surface-200 text-surface-600 hover:border-brand-300 hover:bg-brand-50/50',
                  // A zero chip stays clickable on purpose — tapping it is how a
                  // teacher confirms "nothing here", and it shows the filter-miss
                  // state rather than silently doing nothing.
                  !active && n === 0 && 'opacity-50',
                )}
              >
                {t(labelKey)}
                <span className={cn('tabular-nums', active ? 'text-white/80' : 'text-surface-400')}>{n}</span>
              </button>
            );
          })}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2">
          <MonthPicker
            value={pickedMonth}
            onChange={handleMonthPick}
            aria-label={t('list.window.monthPickerLabel')}
          />
          {!isDefaultWindow && (
            <Button type="button" variant="ghost" size="sm" onClick={handleResetWindow}>
              {t('list.window.reset')}
            </Button>
          )}
        </div>
      </div>

      {/* Search full-width under the filters, sort at the row's end (phones: beside it). */}
      <div className="mb-4 flex items-center gap-3">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-surface-400" />
          <Input
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={t('list.searchPlaceholder')}
            className="pl-9"
          />
        </div>
        <Select value={sortKey} onValueChange={(v) => setSortKey(v as SortKey)}>
          <SelectTrigger className="w-[130px] shrink-0 md:w-[190px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="date-desc">{t('list.sortNewest')}</SelectItem>
            <SelectItem value="date-asc">{t('list.sortOldest')}</SelectItem>
            <SelectItem value="amount-desc">{t('list.sortAmountDesc')}</SelectItem>
            <SelectItem value="amount-asc">{t('list.sortAmountAsc')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* The count, plus the one thing a teacher does not know until told: the
          accents are optional. Suppressed while loading so it cannot flash "0
          kết quả" over a cache that has not landed. */}
      {searchQuery.trim() !== '' && !isLoading && (
        <p className="-mt-1 mb-3 text-[12px] text-surface-500">
          {t('list.searchResults', { count: visibleInvoices.length, q: searchQuery.trim() })}
        </p>
      )}

      <div className={cn('rounded-md border bg-card', multi.containerProps.className)}>
        {isLoading ? (
          <div className="divide-y divide-surface-200">
            <span className="sr-only">{t('list.loading')}</span>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-4">
                <Skeleton className="h-4 w-4 shrink-0" />
                <Skeleton className="h-4 w-40" />
                <Skeleton className="ml-auto h-5 w-16 rounded-full" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        ) : emptyKind === 'error' ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">{t('list.loadError')}</div>
        ) : emptyKind === 'teach' ? (
          <EmptyState
            kind="teach"
            icon={FileText}
            title={t('list.emptyTeachTitle')}
            body={t('list.emptyTeachBody')}
            action={{ label: ti('closeMonth.open'), to: '/invoices/close-month' }}
          />
        ) : emptyKind === 'searchMiss' ? (
          <EmptyState kind="searchMiss" query={searchQuery} hint={t('list.searchHint')} />
        ) : emptyKind === 'filterMiss' ? (
          <EmptyState kind="filterMiss" title={t('list.emptyFilterTitle')} onClear={handleClearFilters} />
        ) : (
          <>
            {/* Desktop table and phone cards are BOTH rendered and switched by
                CSS. useIsMobile() is false on its first render, so branching on
                it would flash the table on a phone; the hidden branch is
                display:none, so it is out of the a11y tree either way. */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allVisibleSelected ? true : someVisibleSelected ? 'indeterminate' : false}
                        onCheckedChange={toggleSelectAllVisible}
                        disabled={visibleInvoices.length === 0}
                        aria-label={t('bulk.bar.selectAll')}
                      />
                    </TableHead>
                    <TableHead>{t('list.client')}</TableHead>
                    <TableHead>{t('list.kind')}</TableHead>
                    <TableHead>{t('list.status')}</TableHead>
                    <TableHead>{t('list.dueDate')}</TableHead>
                    <TableHead className="text-right">{t('list.amount')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleInvoices.map((invoice) => {
                    const late = overdueDays(invoice.dueDate, now);
                    const due = parseDueDate(invoice.dueDate);
                    const matched = autoMatchLine(invoice);
                    return (
                      <TableRow
                        key={invoice.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={(e) => {
                          // Selection mode (THI-180): click-anywhere toggles
                          // instead of navigating while active; navigation
                          // resumes on its own the moment selection returns
                          // to zero, since `multi.active` is read fresh.
                          multi.getRowProps(invoice.id).onClick(e);
                          if (!multi.active) navigate(`/invoices/${invoice.id}`);
                        }}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={multi.isSelected(invoice.id)}
                            onCheckedChange={() => multi.toggle(invoice.id)}
                            aria-label={t('list.selectRow', { code: invoice.invoiceCode })}
                          />
                        </TableCell>
                        <TableCell>
                          <span className="block font-medium text-surface-900">
                            {invoice.client?.name ? (
                              <Highlighted text={invoice.client.name} query={searchQuery} />
                            ) : (
                              t('list.unknownClient')
                            )}
                          </span>
                          <span className="block font-mono text-[11px] text-muted-foreground">
                            <Highlighted text={invoice.invoiceCode} query={searchQuery} />
                          </span>
                          <StudentMatchLine students={invoice.students} query={searchQuery} />
                        </TableCell>
                        <TableCell>{kindLabel(invoice)}</TableCell>
                        <TableCell>
                          {getStatusBadge(invoice)}
                          {matched && (
                            <span className="mt-1 block text-[11px] text-brand-700">
                              <Sparkles className="mr-1 inline-block h-3 w-3 align-[-1px]" aria-hidden />
                              {matched}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className={invoice.isOverdue ? 'font-medium text-destructive' : 'text-surface-500'}>
                          {due ? DUE_DATE_FMT.format(due) : '—'}
                          {invoice.isOverdue && late > 0 && (
                            <span className="whitespace-nowrap"> · {t('list.overdueSuffix', { count: late })}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-medium tabular-nums text-surface-900">
                          {formatVnd(invoice.totalAmount)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="divide-y divide-surface-200 md:hidden">
              {visibleInvoices.map((invoice) => {
                const late = overdueDays(invoice.dueDate, now);
                const due = parseDueDate(invoice.dueDate);
                const matched = autoMatchLine(invoice);
                return (
                  <div key={invoice.id} className="flex items-start gap-2 px-3 py-2">
                    {/* 44px hit target, pinned in px (--spacing is 0.2rem). */}
                    <span className="flex min-h-[44px] min-w-[44px] items-center justify-center">
                      <Checkbox
                        checked={multi.isSelected(invoice.id)}
                        onCheckedChange={() => multi.toggle(invoice.id)}
                        aria-label={t('list.selectRow', { code: invoice.invoiceCode })}
                      />
                    </span>
                    {/* A button, not a div with onClick: the whole card is one
                        tap target to the detail, and the checkbox beside it
                        stays a separate control instead of nesting inside it.
                        Mobile has no shift key, so this is always a plain
                        toggle while selection mode is active (THI-180). */}
                    <button
                      type="button"
                      onClick={(e) => {
                        multi.getRowProps(invoice.id).onClick(e);
                        if (!multi.active) navigate(`/invoices/${invoice.id}`);
                      }}
                      className="min-w-0 flex-1 py-1 text-left"
                    >
                      <span className="flex items-baseline gap-2">
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-surface-900">
                          {invoice.client?.name ? (
                            <Highlighted text={invoice.client.name} query={searchQuery} />
                          ) : (
                            t('list.unknownClient')
                          )}
                        </span>
                        <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                          <Highlighted text={invoice.invoiceCode} query={searchQuery} />
                        </span>
                      </span>
                      <StudentMatchLine students={invoice.students} query={searchQuery} />
                      <span className="mt-1.5 flex flex-wrap items-center gap-2">
                        {kindLabel(invoice)}
                        {getStatusBadge(invoice)}
                        <span className={cn('text-[12px]', invoice.isOverdue ? 'font-medium text-destructive' : 'text-surface-500')}>
                          {due ? DUE_DATE_FMT.format(due) : '—'}
                          {invoice.isOverdue && late > 0 && ` · ${t('list.overdueSuffix', { count: late })}`}
                        </span>
                      </span>
                      <span className="mt-1.5 block text-right text-sm font-semibold tabular-nums text-surface-900">
                        {formatVnd(invoice.totalAmount)}
                      </span>
                      {matched && (
                        <span className="mt-1 block text-[12px] text-brand-700">
                          <Sparkles className="mr-1 inline-block h-3 w-3 align-[-1px]" aria-hidden />
                          {matched}
                        </span>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <BulkActionsBar
        invoices={invoices ?? []}
        selectedIds={selectedIds}
        onClearSelection={() => setSelectedIds(new Set())}
      />
    </div>
  );
}
